import claimApi from 'Agent/Claims/api/claimApi';
import {Claim} from 'Agent/Claims/models/Claim';
import {AsyncPage, Empty, Async} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {constant} from 'fp-ts/lib/function';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {takeLatest} from 'redux-saga/effects';
import {createAction} from 'Shared/utilities/actionCreator';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getPageApiCallGenerator, PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {Check} from 'Agent/Claims/models/Check';

export enum ClaimActionType {
  GET_CLAIMS = 'claim/GET_CLAIMS',
  CHANGE_CLAIMS = 'claim/CHANGE_CLAIMS',
  CHANGE_CLAIM_FILTERS = 'claim/CHANGE_CLAIM_FILTERS',
  GET_CLAIM_CHECKS = 'claim/GET_CLAIM_CHECKS',
  CHANGE_CLAIM_CHECKS = 'claim/CHANGE_CLAIM_CHECKS',
}

export type FilterEnum = {
  name: string;
  value: string;
};

export const EmptyClaimFilter: ClaimFilters = {
  agentNumber: '',
  name: '',
};

export type ClaimFilters = {
  name: string;
  agentNumber: string;
};

export const changeClaimFilters = (filters: ClaimFilters) =>
  createAction(ClaimActionType.CHANGE_CLAIM_FILTERS, {filters});

export const getClaims = (
  agentNumbers: ValidAgentNumber[],
  isNewYork: boolean,
  encryptedIdentifier: string,
  policyId: string,
  pageOption?: Page<Claim>,
  filter?: ClaimFilters
) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );

  return createAction(ClaimActionType.GET_CLAIMS, {...payload, policyId, agentNumbers, isNewYork, encryptedIdentifier});
};
export const changeClaimChecks = (checks: Async<Check[]>) =>
  createAction(ClaimActionType.CHANGE_CLAIM_CHECKS, {checks});
export const getClaimChecks = (claim: Claim, encryptedIdentifier: string) =>
  createAction(ClaimActionType.GET_CLAIM_CHECKS, {claim, encryptedIdentifier});
export const changeClaims = (claims: AsyncPage<Page<Claim>>) => createAction(ClaimActionType.CHANGE_CLAIMS, claims);

type ClaimState = {
  policyNumber: string;
  claims: AsyncPage<Page<Claim>>;
  claimsFilter: ClaimFilters;
  checks: Async<Check[]>;
};

export const DEFAULT_STATE: ClaimState = {
  claims: new Empty(),
  claimsFilter: EmptyClaimFilter,
  policyNumber: '',
  checks: new Empty(),
};

type ClaimAction =
  | ReturnType<typeof changeClaimChecks>
  | ReturnType<typeof changeClaimFilters>
  | ReturnType<typeof getClaims>
  | ReturnType<typeof changeClaims>;

export default (state: ClaimState = DEFAULT_STATE, action: ClaimAction): ClaimState => {
  switch (action.type) {
    case ClaimActionType.CHANGE_CLAIM_CHECKS:
      return {
        ...state,
        checks: action.payload.checks,
      };
    case ClaimActionType.CHANGE_CLAIM_FILTERS:
      return {
        ...state,
        claimsFilter: action.payload.filters,
      };
    case ClaimActionType.CHANGE_CLAIMS:
      return {
        ...state,
        claims: action.payload,
      };
    default:
      return state;
  }
};

export const getClaimsFromApi = (action: ReturnType<typeof getClaims>) => {
  return PageFetchAction.match({
    Additional: additionalPage => mergeWithNextPage<Claim>(additionalPage),
    Initial: () =>
      claimApi.getClaims(
        action.payload.agentNumbers.map(prop('value')),
        action.payload.isNewYork,
        action.payload.encryptedIdentifier,
        action.payload.policyId,
        action.payload.filter
      ),
  })(action.payload);
};

export function* watchFetchClaims() {
  yield takeLatest(ClaimActionType.GET_CLAIMS, getPageApiCallGenerator(getClaimsFromApi, changeClaims));
}
